import { format } from 'date-fns'
import { Button } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useState, useEffect, SetStateAction, Dispatch } from 'react'

import Chip from '../common/chip'
import { BankingPartner } from '../../types/user.types'
import BankIcon from '../../assets/icons/bank-icon.svg'
import { BankAccount, Customer, Payment } from '../../types'
import AccountIcon from '../../assets/icons/account-icon.svg'
import ClarifyIcon from '../../assets/icons/clarify-icon.svg'
import DocumentIcon from '../../assets/icons/document-icon.svg'
import ContactsIcon from '../../assets/icons/contacts-icon.svg'
import '../payment-to-review-details-management/styles/index.css'
import MoneyGreenIcon from '../../assets/icons/money-icon-green.svg'
import PaymentStatusIcon from '../../assets/icons/payment-status-icon.svg'
import { numberWithCommas, numberWithDots } from '../../utils/common.utils'
import { customersBankAccountType, customersDocumentType } from '../../lib'
import PaymentDecisionIcon from '../../assets/icons/payment-decision-icon.svg'
import { baseButtonBlueBg, baseButtonWarning } from '../../theme/custom-styles'
import PaymentReferenceIcon from '../../assets/icons/payment-reference-icon.svg'
import { PaymentPendingRoutingTableRow } from './payment-pending-routing-table-row'
import BackgroundResearchIcon from '../../assets/icons/background-research-icon.svg'

export const PaymentPendingRoutingDetailsTable = ({
  paymentData,
  additionalInfo,
  setRoutingWarning,
  paymentCustomerData,
  handleReturnPayment,
  handleApprovePayment,
  handleDownloadAttachmentFile
}: {
  additionalInfo: Array<any>
  paymentData: Payment | null
  handleReturnPayment: () => void
  handleApprovePayment: () => void
  paymentCustomerData: Customer | null
  setRoutingWarning: Dispatch<SetStateAction<string>>
  handleDownloadAttachmentFile: (attachmentFilename: string) => void
}) => {
  const { t } = useTranslation(['payments-to-review-management'])

  const [routingReason, setRoutingReason] = useState('-')
  const [paymentDecision, setPaymentDecision] = useState('pending')
  const [paymentAdditionalData, setPaymentAdditionalData] = useState('-')
  const [bankingPartnerUsed, setBankingPartnerUsed] = useState<BankingPartner | null | undefined>(null)
  const [bankAccountData, setBankAccountData] = useState<Partial<BankAccount> | null | undefined>(null)

  useEffect(() => {
    if (paymentData && paymentCustomerData) {
      const banksAccountsDataByCustomer =
        Array.isArray(paymentCustomerData.bankAccountsData) && paymentCustomerData.bankAccountsData.length > 0
          ? paymentCustomerData.bankAccountsData?.find((bankAccount) => {
              if (bankAccount.bankNumber === paymentData.bankAccountNumber) {
                // Esto lo hacemos por el formato para el cliente
                if (bankAccount.bankAccountType === '27') {
                  bankAccount.bankAccountType = '1'
                } else if (bankAccount.bankAccountType === '37') {
                  bankAccount.bankAccountType = '2'
                } else if (bankAccount.bankAccountType === '1') {
                  bankAccount.bankAccountType = '1'
                } else if (bankAccount.bankAccountType === '2') {
                  bankAccount.bankAccountType = '2'
                } else {
                  bankAccount.bankAccountType = '3'
                }

                return bankAccount
              }

              return null
            })
          : paymentData?.bankAccountType && paymentData.bankName && paymentData.bankAccountNumber
          ? {
              bankName: paymentData.bankName,
              bankNumber: paymentData.bankAccountNumber,
              // Esto lo hacemos por el formato para el cliente
              bankAccountType: paymentData.bankAccountType === '27' ? '1' : paymentData.bankAccountType === '37' ? '2' : '3'
            }
          : null

      setBankAccountData(banksAccountsDataByCustomer)
    }

    if (paymentData) {
      if (!('rejectedByAllBankingPartners' in paymentData) || paymentData.rejectedByAllBankingPartners === false) {
        setRoutingReason(`Demora en la respuesta de riel desde ${format(new Date(paymentData.createdAt as Date), 'dd/MM/yyyy | HH:mm:ss')}`)
      }

      if (paymentData.rejectedByAllBankingPartners === true) {
        setRoutingReason('Por cumplimiento, beneficiario está en lista negra ó gris')
      }

      if ('routingData' in paymentData && paymentData.routingData?.approved) {
        setPaymentDecision('accepted')
      }

      if ('routingData' in paymentData && paymentData.routingData?.rejected) {
        setPaymentDecision('rejected')
      }
    }

    if (paymentData && 'bankingPartnerUsed' in paymentData) {
      const bankingPartnerUsedToSet = paymentData.bankingPartnerUsed

      if (
        bankingPartnerUsedToSet &&
        typeof bankingPartnerUsedToSet === 'object' &&
        'referenceNumberSent' in bankingPartnerUsedToSet &&
        bankingPartnerUsedToSet.referenceNumberSent !== '' &&
        paymentDecision !== 'accepted' &&
        paymentDecision !== 'rejected'
      ) {
        setRoutingWarning(
          t('paymentPendingRouting.decisionWarning', {
            bankingPartner: String(bankingPartnerUsed?.name).toUpperCase(),
            referenceNumberSent: bankingPartnerUsed?.referenceNumberSent ? String(bankingPartnerUsed?.referenceNumberSent) : 'N/A',
            ns: 'payments-to-review-management'
          })
        )
      }

      setBankingPartnerUsed(bankingPartnerUsedToSet as BankingPartner)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentData, paymentCustomerData, paymentDecision])

  useEffect(() => {
    if (additionalInfo && paymentCustomerData) {
      const paymentCustomerAdditionalInfo = additionalInfo.filter((data) => paymentCustomerData.customerCompanyPocketsRelated && paymentCustomerData.customerCompanyPocketsRelated.includes(String(data._id)))
      const paymentCustomerAdditionalInfoTexts = paymentCustomerAdditionalInfo.length > 0 ? paymentCustomerAdditionalInfo.map((data) => data.name) : []

      if (paymentCustomerAdditionalInfoTexts.length > 0) {
        setPaymentAdditionalData(paymentCustomerAdditionalInfoTexts.join(', '))
        return
      }

      setPaymentAdditionalData('-')
    }
  }, [additionalInfo, paymentCustomerData])

  return (
    <div
      style={{ marginTop: '16px' }}
      className="payment-to-review-details-table-container"
    >
      <div
        style={{ marginTop: '0px' }}
        className="table-header-container"
      >
        <div className="left-content">
          <div className="icon-container">
            <img
              src={AccountIcon}
              alt="account-icon"
            />
          </div>
          <div className="header-info-subtitle">{t('noveltiesTable.customerName')}</div>
        </div>
        <div className="right-content">
          <div className="header-info-data">{paymentData && paymentData.customerRegisteredFullName ? paymentData.customerRegisteredFullName : t('noveltiesTable.unidentified').toUpperCase()}</div>
        </div>
      </div>
      <div className="table-body-container">
        <PaymentPendingRoutingTableRow
          icon={PaymentReferenceIcon}
          subtitle={t('noveltiesTable.referenceNumber')}
          infoData={String(paymentData?.referenceNumber)}
        />
        <PaymentPendingRoutingTableRow
          icon={ContactsIcon}
          subtitle={t('noveltiesTable.idSubtitleName')}
          infoData={
            paymentCustomerData && paymentCustomerData.documentType === 'CE'
              ? `${(customersDocumentType as any)['E']}: ${numberWithDots(String(paymentCustomerData.documentNumber))}`
              : paymentCustomerData && (customersDocumentType as any)[paymentCustomerData.documentType ?? 'C'] !== 'undefined' && numberWithDots(String(paymentCustomerData.documentNumber)) !== 'undefined'
              ? `${(customersDocumentType as any)[paymentCustomerData.documentType ?? 'C']}: ${numberWithDots(String(paymentCustomerData.documentNumber))}`
              : ''
          }
        />
        <PaymentPendingRoutingTableRow
          icon={BankIcon}
          subtitle={t('noveltiesTable.bankAccount')}
          infoData={
            bankAccountData && typeof (customersBankAccountType as any)[bankAccountData.bankAccountType ?? '3'] !== 'undefined' && bankAccountData.bankName !== 'undefined' && bankAccountData.bankNumber !== 'undefined'
              ? `${(customersBankAccountType as any)[bankAccountData.bankAccountType ?? '3']} | ${bankAccountData.bankName} - No. ${bankAccountData.bankNumber}`
              : ''
          }
        />
        <PaymentPendingRoutingTableRow
          icon={MoneyGreenIcon}
          subtitle={t('noveltiesTable.amountToPay')}
          infoData={paymentData?.amount && !isNaN(Number(paymentData?.amount)) && Number(paymentData?.amount) > 0 ? `$ ${numberWithCommas(String(paymentData?.amount))} COP` : ''}
        />
        {paymentAdditionalData && paymentAdditionalData !== '-' && (
          <PaymentPendingRoutingTableRow
            icon={BackgroundResearchIcon}
            infoData={paymentAdditionalData}
            subtitle={t('noveltiesTable.additionalInfo')}
          />
        )}
        <PaymentPendingRoutingTableRow
          icon={ClarifyIcon}
          infoData={routingReason}
          subtitle={t('noveltiesTable.routingReason')}
        />
        <PaymentPendingRoutingTableRow
          icon={PaymentStatusIcon}
          subtitle={t('noveltiesTable.status')}
          infoData={
            <Chip
              status={paymentData?.status ?? 'reviewed'}
              label={
                paymentData?.status === 'reviewed'
                  ? t('paymentStatus.reviewed', { ns: 'document-payments-details-management' })
                  : paymentData?.status?.indexOf('error') !== -1
                  ? t('paymentStatus.error', { ns: 'document-payments-details-management' })
                  : paymentData?.status === 'paid'
                  ? t('paymentStatus.paid', { ns: 'document-payments-details-management' })
                  : paymentData?.status === 'processing'
                  ? t('paymentStatus.processing', { ns: 'document-payments-details-management' })
                  : paymentData?.status === 'rejected'
                  ? t('paymentStatus.rejected', { ns: 'document-payments-details-management' })
                  : paymentData?.status === 'returned'
                  ? t('paymentStatus.returned', { ns: 'document-payments-details-management' })
                  : paymentData?.status === 'waiting-for-resolution'
                  ? t('paymentStatus.waitingForResolution', { ns: 'document-payments-details-management' })
                  : paymentData?.status === 'validated'
                  ? t('paymentStatus.validated', { ns: 'document-payments-details-management' })
                  : t('paymentStatus.forReview', { ns: 'document-payments-details-management' })
              }
            />
          }
        />
        <PaymentPendingRoutingTableRow
          icon={PaymentDecisionIcon}
          subtitle={t('noveltiesTable.paymentDecision')}
          infoData={
            <div className="routing-payment-decision">
              <Chip
                status={paymentDecision ?? 'pending'}
                label={
                  paymentDecision === 'accepted'
                    ? t('paymentStatus.accepted', { ns: 'document-payments-details-management' })
                    : paymentDecision === 'pending'
                    ? t('paymentStatus.pending', { ns: 'document-payments-details-management' })
                    : t('paymentStatus.rejected', { ns: 'document-payments-details-management' })
                }
              />
              {bankingPartnerUsed && 'referenceNumberSent' in bankingPartnerUsed && bankingPartnerUsed.referenceNumberSent !== '' && paymentDecision !== 'accepted' && paymentDecision !== 'rejected' && (
                <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '400px', gap: '15px', color: '#F59E0B', fontWeight: '500', wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                  {t('paymentPendingRouting.decisionWarning', {
                    bankingPartner: String(bankingPartnerUsed?.name).toUpperCase(),
                    referenceNumberSent: bankingPartnerUsed?.referenceNumberSent ? String(bankingPartnerUsed?.referenceNumberSent) : 'N/A',
                    ns: 'payments-to-review-management'
                  })}
                </div>
              )}
              {(paymentDecision === 'accepted' || paymentDecision === 'rejected') && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div>
                      <div>{t('paymentPendingRouting.decisionDate', { ns: 'payments-to-review-management' })}</div>
                      <div>{`${format(new Date(paymentData?.routingData?.decisionDate as Date), 'dd/MM/yyyy | HH:mm:ss')}`}</div>
                    </div>
                    <div>
                      <div>{t('paymentPendingRouting.decisionBy', { ns: 'payments-to-review-management' })}</div>
                      <div>{paymentDecision === 'accepted' ? paymentData?.routingData?.approvedBy?.name.toUpperCase() : paymentData?.routingData?.rejectedBy?.name.toUpperCase()}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>{paymentData?.routingData?.decisionReason}</div>
                </div>
              )}
              {paymentDecision === 'pending' && (
                <div style={{ display: 'flex', gap: '15px' }}>
                  <Button
                    variant="text"
                    sx={baseButtonBlueBg}
                    onClick={handleApprovePayment}
                  >
                    {t('sendPayment')}
                  </Button>
                  <Button
                    variant="contained"
                    sx={baseButtonWarning}
                    onClick={handleReturnPayment}
                  >
                    {t('returnPayment')}
                  </Button>
                </div>
              )}
            </div>
          }
        />
        {paymentData && paymentData.routingData?.voucher && (
          <PaymentPendingRoutingTableRow
            icon={DocumentIcon}
            subtitle={t('noveltiesTable.voucher')}
            infoData={
              <div
                onClick={() => handleDownloadAttachmentFile(String(paymentData.routingData?.voucher?.systemFilename))}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', margin: '8px 0px', gap: '10px' }}
              >
                <img
                  src={DocumentIcon}
                  alt="document-icon"
                />
                <div style={{ color: '#00e', textDecorationLine: 'underline' }}>{paymentData.routingData?.voucher?.originalFilename}</div>
              </div>
            }
          />
        )}
      </div>
    </div>
  )
}
